// ============================================================================
// Refund worklist — the /cash-log "Refunds" tab.
//
// One row per refund raised on the OMS screen, actionable inline: attach the
// bank account, or release the unpaid amount back to the wallet. A row expands only when it has payout
// history (or a legacy multi-leg refund) to show.
// ============================================================================
import {
  Alert,
  Button,
  DatePicker,
  Dropdown,
  Empty,
  Form,
  Input,
  Modal,
  Radio,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { MenuProps } from "antd";
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  DownloadOutlined,
  MoreOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { AttachBankModal, RefundPayToCell } from "../oms/refund/components/refund_bank";
import {
  PayingAccount,
  RefundPaymentLeg,
  RefundRow,
  RefundStatus,
  RefundTab,
  ReleaseReason,
  attachRefundBank,
  fetchPayingAccounts,
  fetchRefunds,
  markRefundPaid,
  releaseRefund,
} from "./refunds.api";

const ACCOUNT_STORAGE_KEY = "cashlog.refund.paidFromAccount";

const readStoredAccount = () => {
  try {
    return window.localStorage.getItem(ACCOUNT_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
};

const MarkPaidModal: React.FC<{
  row: RefundRow | null;
  onClose: () => void;
  onDone: (row: RefundRow) => void;
}> = ({ row, onClose, onDone }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState<PayingAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const outstanding = row ? Number(row.payments[0]?.outstanding_rupees || 0) : 0;

  useEffect(() => {
    if (!row) return;
    const stored = readStoredAccount();
    form.setFieldsValue({
      amount_rupees: outstanding ? outstanding.toFixed(2) : undefined,
      utr: "",
      paid_on: dayjs(),
      from_account_id: stored,
    });
    setLoadingAccounts(true);
    fetchPayingAccounts().then((list) => {
      setAccounts(list);
      setLoadingAccounts(false);
      const current = form.getFieldValue("from_account_id");
      if (!list.some((a) => a.account_id === current)) {
        form.setFieldValue("from_account_id", list.length === 1 ? list[0].account_id : undefined);
      }
    });
  }, [row, form, outstanding]);

  const submit = async () => {
    if (!row) return;
    const values = await form.validateFields();
    setSaving(true);
    const updated = await markRefundPaid(row.id, {
      utr: values.utr.trim(),
      amount_paise: String(Math.round(Number(values.amount_rupees) * 100)),
      paid_on: values.paid_on.format("YYYY-MM-DD"),
      from_account_id: values.from_account_id,
    });
    setSaving(false);
    if (updated) {
      try {
        window.localStorage.setItem(ACCOUNT_STORAGE_KEY, values.from_account_id);
      } catch {
        /* storage blocked */
      }
      onDone(updated);
      onClose();
    }
  };

  const bank = row?.destination?.bank_details;

  return (
    <Modal
      open={!!row}
      title={row ? `Mark refund #${row.id} as paid` : ""}
      okText="Mark as paid"
      confirmLoading={saving}
      onOk={submit}
      onCancel={saving ? undefined : onClose}
      destroyOnClose
      width={520}
    >
      {row && (
        <>
          <div style={{ marginBottom: 12 }}>
            <Typography.Text strong>{row.customer_name || "Customer"}</Typography.Text>
            <span style={muted}> · {formatRupees(outstanding)} still to pay</span>
            {bank ? (
              <div style={muted}>
                To {bank.account_holder_name} · {bank.bank_label || bank.bank_name} ·{" "}
                {bank.account_number} · {bank.ifsc}
              </div>
            ) : (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 8 }}
                message="No bank account is attached to this refund — check where the money went before recording it."
              />
            )}
          </div>
          <Form form={form} layout="vertical" requiredMark>
            <Form.Item
              name="utr"
              label="UTR"
              rules={[{ required: true, whitespace: true, message: "Enter the bank UTR" }]}
            >
              <Input placeholder="e.g. HDFCN52026101512345" maxLength={64} autoFocus />
            </Form.Item>
            <Space size={12} style={{ display: "flex" }} align="start">
              <Form.Item
                name="amount_rupees"
                label="Amount refunded (₹)"
                style={{ flex: 1 }}
                rules={[
                  { required: true, message: "How much was paid?" },
                  {
                    validator: (_, v) =>
                      Number(v) > 0 && Number(v) <= outstanding
                        ? Promise.resolve()
                        : Promise.reject(new Error(`Between ₹0.01 and ${formatRupees(outstanding)}`)),
                  },
                ]}
              >
                <Input type="number" step="0.01" min={0} prefix="₹" />
              </Form.Item>
              <Form.Item
                name="paid_on"
                label="Refund date"
                rules={[{ required: true, message: "When was it paid?" }]}
              >
                <DatePicker
                  format="DD MMM YYYY"
                  allowClear={false}
                  disabledDate={(d) => !!d && d.isAfter(dayjs(), "day")}
                  style={{ width: 170 }}
                />
              </Form.Item>
            </Space>
            <Form.Item
              name="from_account_id"
              label="Paid from (Zoho account)"
              rules={[{ required: true, message: "Pick the account the money left from" }]}
              style={{ marginBottom: 4 }}
            >
              <Select
                showSearch
                loading={loadingAccounts}
                placeholder={loadingAccounts ? "Loading accounts…" : "Select an account"}
                optionFilterProp="label"
                options={accounts.map((a) => ({
                  value: a.account_id,
                  label: a.account_type ? `${a.account_name} (${a.account_type})` : a.account_name,
                }))}
              />
            </Form.Item>
          </Form>
          <div style={muted}>
            Recorded as a refund on the credit note in Zoho, then shown here straight away. Pay part
            now and the rest stays on the row.
          </div>
        </>
      )}
    </Modal>
  );
};

const STATUS_COLOUR: Record<RefundStatus, string> = {
  pending: "orange",
  partial: "gold",
  partially_settled: "gold",
  settled: "green",
  failed: "red",
  released: "default",
};

const STATUS_LABEL: Record<RefundStatus, string> = {
  pending: "Pending",
  partial: "Part paid",
  partially_settled: "Part paid",
  settled: "Settled",
  failed: "Failed",
  released: "Back to wallet",
};

const RELEASE_REASON_LABEL: Record<ReleaseReason, string> = {
  payout_failed: "Payout failed",
  cancelled: "Refund cancelled",
};


const muted: React.CSSProperties = { color: "#8c8c8c", fontSize: 12 };

const formatRupees = (v: string | number) =>
  `₹${Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const StatusTag: React.FC<{ status: RefundStatus }> = ({ status }) => (
  <Tag color={STATUS_COLOUR[status]}>{STATUS_LABEL[status]}</Tag>
);

// ---------------------------------------------------------------------------
// Row actions.
// ---------------------------------------------------------------------------
const confirmRelease = (row: RefundRow, onDone: () => void) => {
  let reason: ReleaseReason = "payout_failed";
  let note = "";
  Modal.confirm({
    title: `Release refund #${row.id} back to the wallet?`,
    width: 480,
    content: (
      <>
        <p style={{ marginBottom: 8 }}>
          The unpaid amount goes back to the customer's wallet and the row moves to "Back to wallet".
          A live Zoho check runs first — if the refund was already paid there, this is refused.
        </p>
        <Radio.Group
          defaultValue={reason}
          style={{ marginBottom: 8 }}
          onChange={(e) => {
            reason = e.target.value;
          }}
          options={[
            { value: "payout_failed", label: RELEASE_REASON_LABEL.payout_failed },
            { value: "cancelled", label: RELEASE_REASON_LABEL.cancelled },
          ]}
        />
        <Input.TextArea
          rows={2}
          placeholder="What happened? (e.g. account closed, customer wants store credit)"
          onChange={(e) => {
            note = e.target.value;
          }}
        />
      </>
    ),
    okText: "Release to wallet",
    okButtonProps: { danger: true },
    onOk: async () => {
      if (await releaseRefund(row.id, reason, note)) onDone();
    },
  });
};

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  zoho_credit_note: "Zoho credit note",
};

// ---------------------------------------------------------------------------
// Payout history of one refund — only shown once something has been paid, or when a legacy refund
// still carries more than one leg.
// ---------------------------------------------------------------------------
const REFERENCE_LABEL: Record<string, string> = {
  utr: "Bank UTR",
  zoho_refund: "Zoho refund",
  gateway_refund: "Gateway refund",
  credit_reversal: "Credit reversal",
};

const PayoutHistory: React.FC<{ row: RefundRow }> = ({ row }) => {
  const leg = row.payments[0];
  const outstanding = Number(leg?.outstanding_rupees || 0);
  return (
    <div style={{ padding: "4px 0 4px 8px" }}>
      <div style={{ ...muted, fontWeight: 600, letterSpacing: 0.3, marginBottom: 6 }}>
        PAYOUTS
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {(leg?.settlements || []).map((s, i) => (
          <div key={`${s.reference}-${i}`} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <CheckCircleFilled style={{ color: "#52c41a" }} />
            <span style={{ ...num, fontWeight: 600, minWidth: 90 }}>
              {formatRupees(s.amount_rupees)}
            </span>
            <Tag style={{ marginInlineEnd: 0 }}>
              {REFERENCE_LABEL[s.reference_type] || s.reference_type || "Reference"}
            </Tag>
            <CopyText value={s.reference} label="reference" />
            {(s.paid_on || s.settled_at) && (
              <span style={muted}>{dayjs(s.paid_on || s.settled_at).format("DD MMM YYYY")}</span>
            )}
          </div>
        ))}
        {outstanding > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <ClockCircleOutlined style={{ color: "#fa8c16" }} />
            <span style={{ ...num, fontWeight: 600, minWidth: 90 }}>
              {formatRupees(outstanding)}
            </span>
            <span style={muted}>still to pay</span>
          </div>
        )}
      </div>
    </div>
  );
};

const LegTable: React.FC<{
  row: RefundRow;
  onReleased: () => void;
}> = ({ row, onReleased }) => (
  <Table<RefundPaymentLeg>
    rowKey="id"
    size="small"
    pagination={false}
    dataSource={row.payments}
    style={{ maxWidth: 900 }}
    columns={[
      {
        title: "Paid from",
        dataIndex: "payment_method",
        render: (method: string, leg) => (
          <Space size={4}>
            <span>{PAYMENT_METHOD_LABEL[method] || method || "—"}</span>
            {!leg.moves_money && (
              <Tooltip title="A credit reversal lowers what the customer owes. No money moves, so there is nothing to pay or record.">
                <Tag>no payout</Tag>
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        title: "Amount",
        dataIndex: "amount_rupees",
        align: "right" as const,
        render: (v: string) => <span style={num}>{formatRupees(v)}</span>,
      },
      {
        title: "Outstanding",
        dataIndex: "outstanding_rupees",
        align: "right" as const,
        render: (v: string, leg) =>
          leg.status === "settled" ? "—" : <strong style={num}>{formatRupees(v)}</strong>,
      },
      {
        title: "Status",
        dataIndex: "status",
        render: (s: RefundStatus, leg) => (
          <Space size={4}>
            <StatusTag status={s} />
            {leg.failure_reason && (
              <Tooltip title={leg.failure_reason}>
                <Tag color="red">why?</Tag>
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        title: "Payout references",
        render: (_: unknown, leg) =>
          leg.settlements.length ? (
            <Space direction="vertical" size={0}>
              {leg.settlements.map((s, i) => (
                <span key={`${s.reference}-${i}`} style={{ fontSize: 12 }}>
                  {s.reference} <span style={muted}>({formatRupees(s.amount_rupees)})</span>
                </span>
              ))}
            </Space>
          ) : (
            <span style={faint}>—</span>
          ),
      },
      ...(row.payments.length > 1
        ? [
            {
              title: "",
              key: "actions",
              align: "right" as const,
              render: (_: unknown, leg: RefundPaymentLeg) =>
                leg.actionable ? (
                  <Button size="small" onClick={() => confirmRelease(row, onReleased)}>
                    Release to wallet
                  </Button>
                ) : null,
            },
          ]
        : []),
    ]}
  />
);

// ---------------------------------------------------------------------------
const faint: React.CSSProperties = { color: "#bfbfbf" };
const num: React.CSSProperties = { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const twoLine: React.CSSProperties = { lineHeight: "20px", minWidth: 0 };

const isOpen = (s: RefundStatus) =>
  s === "pending" || s === "partial" || s === "partially_settled";

const CopyText: React.FC<{ value: string; label?: string }> = ({ value, label }) => (
  <Typography.Text
    style={{ ...num, fontSize: 13 }}
    copyable={{ text: value, tooltips: [`Copy ${label || ""}`.trim(), "Copied"] }}
  >
    {value}
  </Typography.Text>
);

const csvField = (value: unknown) => {
  if (value == null) return "";
  const s = String(value);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const excelText = (value?: string | null) => (value ? `="${value}"` : "");

const REPORT_HEADERS = [
  "Refund ID",
  "Created At",
  "Customer",
  "Lead ID",
  "Sales Order",
  "Credit Note",
  "Account Holder",
  "Bank",
  "Account Number",
  "IFSC",
  "Bank Source",
  "Payout (Rs)",
  "Paid (Rs)",
  "Outstanding (Rs)",
  "Total (Rs)",
  "Status",
  "Released to Wallet (Rs)",
  "Release Reason",
  "Payout References",
  "Reason",
  "Notes",
  "Created By",
  "Requested By",
];

const reportRow = (r: RefundRow) => {
  const bank = r.destination?.bank_details;
  const references = r.payments
    .flatMap((p) => p.settlements)
    .map((s) => `${REFERENCE_LABEL[s.reference_type] || s.reference_type}: ${s.reference} (${s.amount_rupees})`)
    .join("; ");
  return [
    r.id,
    dayjs(r.created_at).format("DD-MMM-YYYY HH:mm"),
    r.customer_name,
    r.lead_id,
    r.sales_order_id ? `SO ${r.sales_order_number}` : r.origin === "manual_cn_admission" ? "Admitted CN" : "",
    r.cn_number || r.zoho_creditnote_id,
    bank?.account_holder_name,
    bank?.bank_label || bank?.bank_name,
    excelText(bank?.account_number),
    bank?.ifsc,
    bank ? r.destination?.bank_source : "missing",
    Number(r.payout_rupees || 0).toFixed(2),
    Number(r.settled_rupees || 0).toFixed(2),
    (Number(r.payout_rupees || 0) - Number(r.settled_rupees || 0)).toFixed(2),
    Number(r.total_rupees || 0).toFixed(2),
    STATUS_LABEL[r.status] || r.status,
    r.released ? Number(r.released.released_rupees || 0).toFixed(2) : "",
    r.released
      ? [RELEASE_REASON_LABEL[r.released.reason] || r.released.reason, r.released.note].filter(Boolean).join(" — ")
      : "",
    references,
    r.reason_code,
    r.notes,
    r.created_by,
    r.requested_by,
  ];
};

const downloadRefundReport = (data: RefundRow[], label: string) => {
  if (!data.length) {
    message.warning("No refunds to download");
    return;
  }
  const lines = [REPORT_HEADERS, ...data.map(reportRow)].map((cols) => cols.map(csvField).join(","));
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `refunds_${label}_${dayjs().format("YYYY-MM-DD_HH-mm")}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const Refunds: React.FC = () => {
  const [rows, setRows] = useState<RefundRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<RefundTab>("pending");
  const [search, setSearch] = useState("");
  const [attaching, setAttaching] = useState<RefundRow | null>(null);
  const [payingRow, setPayingRow] = useState<RefundRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setRows(await fetchRefunds(tab));
    setLoading(false);
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  // An attach-bank returns the whole refund, so splice it in rather than refetching the list —
  // the operator keeps their scroll position and their expanded row. If the status moved past
  // what this tab shows (shouldn't happen on attach, but a done-tab row still filtered out
  // defensively), drop it.
  const replaceRow = (updated: RefundRow) =>
    setRows((current) => {
      const next = current.map((r) => (r.id === updated.id ? updated : r));
      if (tab === "pending") return next.filter((r) => r.status !== "settled" && r.status !== "released");
      if (tab === "done") return next.filter((r) => r.status === "settled");
      const reason = tab === "failed" ? "payout_failed" : "cancelled";
      return next.filter((r) => r.status === "released" && r.released?.reason === reason);
    });

  const q = search.trim().toLowerCase();
  const visible = q
    ? rows.filter((r) =>
        [
          r.id,
          r.sales_order_number,
          r.lead_id,
          r.customer_name,
          r.cn_number,
          r.destination?.bank_details?.account_holder_name,
          r.destination?.bank_details?.account_number,
          ...r.payments.flatMap((p) => p.settlements.map((x) => x.reference)),
        ].some((v) => v != null && String(v).toLowerCase().includes(q))
      )
    : rows;
  const showPayouts = tab === "done" || visible.some((r) => Number(r.settled_rupees) > 0);

  const outstanding = visible.reduce(
    (sum, r) => sum + Number(r.payout_rupees || 0) - Number(r.settled_rupees || 0),
    0
  );
  const toBank = visible.filter((r) => r.destination?.kind === "customer").length;

  const hasHistory = (row: RefundRow) =>
    row.payments.length > 1 || row.payments.some((p) => p.settlements.length > 0);

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Space wrap size={12}>
          <Input.Search
            allowClear
            placeholder="Search SO, lead, CN, name or account"
            style={{ width: 320 }}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Segmented
            value={tab}
            onChange={(v) => setTab(v as RefundTab)}
            options={[
              { label: "Pending", value: "pending" },
              { label: "Done", value: "done" },
              { label: "Payout failed", value: "failed" },
              { label: "Cancelled", value: "cancelled" },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                { key: "current", label: "Download current view" },
                { key: "all", label: "Download all refunds" },
              ],
              onClick: async ({ key }) => {
                if (key === "current") return downloadRefundReport(visible, "current");
                const hide = message.loading("Fetching all refunds...", 0);
                const all = await fetchRefunds("all");
                hide();
                downloadRefundReport(all, "all");
              },
            }}
          >
            <Button icon={<DownloadOutlined />}>Download report</Button>
          </Dropdown>
        </Space>
        <Space size={24}>
          <div style={{ textAlign: "right", lineHeight: "18px" }}>
            <div style={muted}>Refunds</div>
            <div style={{ fontWeight: 600 }}>
              {visible.length}
              <span style={{ ...muted, fontWeight: 400 }}> · {toBank} to bank</span>
            </div>
          </div>
          <div style={{ textAlign: "right", lineHeight: "18px" }}>
            <div style={muted}>Outstanding</div>
            <div style={{ ...num, fontWeight: 600, fontSize: 16 }}>{formatRupees(outstanding)}</div>
          </div>
        </Space>
      </div>

      <Table<RefundRow>
        rowKey="id"
        size="middle"
        loading={loading}
        dataSource={visible}
        scroll={{ x: 1320 + (showPayouts ? 280 : 0) + (tab === "failed" || tab === "cancelled" ? 260 : 0) }}
        pagination={{ pageSize: 25, showSizeChanger: true, hideOnSinglePage: true }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                tab === "pending"
                  ? "Nothing waiting to be paid out"
                  : tab === "failed"
                  ? "No failed payouts"
                  : tab === "cancelled"
                  ? "No cancelled refunds"
                  : "No refunds paid out yet"
              }
            />
          ),
        }}
        expandable={{
          expandedRowRender: (row) =>
            row.payments.length > 1 ? (
              <LegTable row={row} onReleased={load} />
            ) : (
              <PayoutHistory row={row} />
            ),
          rowExpandable: hasHistory,
          columnWidth: 40,
        }}
        columns={[
          {
            title: "Refund",
            dataIndex: "id",
            width: 110,
            render: (id: number, row) => (
              <div style={twoLine}>
                <div style={{ fontWeight: 600 }}>#{id}</div>
                <Tooltip title={row.notes || undefined}>
                  <span style={muted}>
                    {new Date(row.created_at).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                    })}
                  </span>
                </Tooltip>
              </div>
            ),
          },
          {
            title: "Requested by",
            dataIndex: "requested_by",
            width: 140,
            render: (name: string | null) => (
              <Typography.Text ellipsis={{ tooltip: name }} style={{ display: "block" }}>
                {name || "—"}
              </Typography.Text>
            ),
          },
          {
            title: "Customer",
            key: "customer",
            width: 210,
            render: (_: unknown, row) => (
              <div style={twoLine}>
                <Typography.Text
                  strong
                  ellipsis={{ tooltip: row.customer_name }}
                  style={{ display: "block" }}
                >
                  {row.customer_name || "—"}
                </Typography.Text>
                {row.lead_id ? (
                  <Typography.Text
                    type="secondary"
                    style={{ ...num, fontSize: 12 }}
                    copyable={{ text: row.lead_id, tooltips: ["Copy lead ID", "Copied"] }}
                  >
                    {row.lead_id}
                  </Typography.Text>
                ) : (
                  <span style={muted}>No lead ID</span>
                )}
              </div>
            ),
          },
          {
            title: "Order",
            dataIndex: "sales_order_number",
            width: 160,
            render: (n: string | number, row) =>
              row.sales_order_id ? (
                <div style={twoLine}>
                  <a href={`/oms/${row.sales_order_id}`} style={{ fontWeight: 500 }}>
                    SO {n}
                  </a>
                  <div style={{ ...muted, ...num }}>{row.cn_number || (row.zoho_creditnote_id ? `Zoho ${row.zoho_creditnote_id}` : "No credit note")}</div>
                </div>
              ) : (
                <div style={twoLine}>
                  <Tooltip title="Credit note admitted to the wallet by hand from the client page — not tied to a sales order.">
                    <Tag style={{ marginInlineEnd: 0 }}>
                      {row.origin === "manual_cn_admission" ? "Admitted CN" : "No order"}
                    </Tag>
                  </Tooltip>
                  {row.zoho_creditnote_id && (
                    <div style={{ ...muted, ...num }}>Zoho {row.zoho_creditnote_id}</div>
                  )}
                </div>
              ),
          },
          {
            // Where the money goes. For a bank refund: the exact account number + IFSC finance
            // types into the payout tool, each one click to copy.
            title: "Pay to",
            key: "pay_to",
            width: 340,
            render: (_: unknown, row: RefundRow) => (
              <RefundPayToCell row={row} onAttach={() => setAttaching(row)} />
            ),
          },
          ...(showPayouts
            ? [
                {
                  title: "Payout",
                  key: "payout",
                  width: 280,
                  render: (_: unknown, row: RefundRow) => {
                    const paid = row.payments.flatMap((p) => p.settlements);
                    if (!paid.length) return <span style={faint}>—</span>;
                    return (
                      <Space direction="vertical" size={6} style={{ width: "100%" }}>
                        {paid.map((p, i) => (
                          <div key={`${p.reference}-${i}`} style={twoLine}>
                            <div style={{ display: "flex", gap: 6, alignItems: "baseline", minWidth: 0 }}>
                              <Typography.Text
                                strong
                                style={{ ...num, fontSize: 13 }}
                                copyable={{ text: p.reference, tooltips: ["Copy UTR", "Copied"] }}
                              >
                                {p.reference}
                              </Typography.Text>
                              <span style={{ ...num, fontSize: 12, color: "#389e0d" }}>
                                {formatRupees(p.amount_rupees)}
                              </span>
                            </div>
                            <div style={muted}>
                              {[
                                p.paid_on || p.settled_at
                                  ? dayjs(p.paid_on || p.settled_at).format("DD MMM YYYY")
                                  : null,
                                p.paid_from ? `from ${p.paid_from}` : null,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                          </div>
                        ))}
                      </Space>
                    );
                  },
                },
              ]
            : []),
          ...(tab === "failed" || tab === "cancelled"
            ? [
                {
                  title: "Remarks",
                  key: "remarks",
                  width: 260,
                  render: (_: unknown, row: RefundRow) =>
                    row.released?.note ? (
                      <div style={twoLine}>
                        <Typography.Paragraph
                          ellipsis={{ rows: 2, tooltip: row.released.note }}
                          style={{ marginBottom: 0 }}
                        >
                          {row.released.note}
                        </Typography.Paragraph>
                        {row.released.released_at && (
                          <div style={muted}>{dayjs(row.released.released_at).format("DD MMM YYYY, HH:mm")}</div>
                        )}
                      </div>
                    ) : (
                      <span style={faint}>—</span>
                    ),
                },
              ]
            : []),
          {
            title: "Amount",
            key: "amount",
            align: "right" as const,
            width: 140,
            render: (_: unknown, row) => (
              <div style={twoLine}>
                <div style={{ ...num, fontWeight: 600, fontSize: 15 }}>
                  {formatRupees(row.payout_rupees)}
                </div>
                {row.released && (
                  <div style={{ ...muted, ...num }}>
                    {formatRupees(row.released.released_rupees)} back to wallet
                  </div>
                )}
                {Number(row.settled_rupees) > 0 ? (
                  <div style={{ ...num, fontSize: 12, color: "#389e0d" }}>
                    {formatRupees(row.settled_rupees)} paid
                  </div>
                ) : row.payout_rupees !== row.total_rupees ? (
                  <div style={{ ...muted, ...num }}>of {formatRupees(row.total_rupees)}</div>
                ) : null}
              </div>
            ),
          },
          {
            title: "Status",
            dataIndex: "status",
            width: 110,
            render: (s: RefundStatus, row) =>
              row.released ? (
                <Tooltip
                  title={
                    <>
                      <div>{RELEASE_REASON_LABEL[row.released.reason] || row.released.reason}</div>
                      {row.released.note && <div>{row.released.note}</div>}
                      {row.released.released_at && (
                        <div style={{ fontSize: 11, opacity: 0.8 }}>
                          {dayjs(row.released.released_at).format("DD MMM YYYY, HH:mm")}
                        </div>
                      )}
                    </>
                  }
                >
                  <Space direction="vertical" size={2}>
                    <StatusTag status={s} />
                    <span style={muted}>
                      {RELEASE_REASON_LABEL[row.released.reason] || row.released.reason}
                    </span>
                  </Space>
                </Tooltip>
              ) : (
                <StatusTag status={s} />
              ),
          },
          {
            title: "",
            key: "actions",
            width: 170,
            render: (_, row) => {
              if (!isOpen(row.status)) return null;
              const items: MenuProps["items"] = [
                { key: "paid", label: "Mark as paid" },
                {
                  key: "bank",
                  label: row.destination?.bank_details ? "Change bank account" : "Add bank account",
                },
                { key: "release", label: "Release to wallet", danger: true },
              ];
              const onMenu: MenuProps["onClick"] = ({ key }) => {
                if (key === "release") confirmRelease(row, load);
                if (key === "bank") setAttaching(row);
                if (key === "paid") setPayingRow(row);
              };
              return (
                <Space size={6}>
                  <Button size="small" type="primary" onClick={() => setPayingRow(row)}>
                    Mark as paid
                  </Button>
                  <Dropdown menu={{ items, onClick: onMenu }} trigger={["click"]}>
                    <Button size="small" icon={<MoreOutlined />} />
                  </Dropdown>
                </Space>
              );
            },
          },
        ]}
      />

      <MarkPaidModal row={payingRow} onClose={() => setPayingRow(null)} onDone={replaceRow} />
      <AttachBankModal
        row={attaching}
        attach={(r, bankId) => attachRefundBank(r.id, bankId)}
        onClose={() => setAttaching(null)} onDone={replaceRow} />
    </div>
  );
};

export default Refunds;
